import {
  ALL_TABLES_RES_NAME,
  APPROVALS_PRIORITY,
  FGA_POLICY_TYPE_PREFIX,
  IAM_ACCESS_ITEM,
  LAKE_FORMATION_TAG_ITEM,
  REDSHIFT_TAG_ITEM,
  RESOURCE_ITEM,
  TABLE_RESOURCE_TYPE,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import {
  AdditionalMetadata,
  Boolean,
  D3ADataAgreement,
  DateString,
  GetDataPermissionRequestsRequest,
  GetDataPermissionRequestsResult,
  GetRequestsRequest,
  GetRequestsResult,
  GetTemplateForResourceResult,
  GladstoneRequestItem,
  ListRequestContextsRequest,
  ListRequestContextsResponse,
  MatchedTemplateWorkflow,
  Rule,
  SimTicket,
  String,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  getDataPermissionRequests,
  getRequestedByRequests,
  getTemplateForResource,
  listRequestContexts,
} from 'src/api/permissions';
import { generateFGAPArn } from 'src/components/utils/arnUtil';
import { extractApprovalTemplateIdFromLink } from 'src/components/permissions/myPermissions/createLakeFormationPermissionsPage';
import { activeGroupOrWorkspaceId, signedInWithWorkspace } from 'src/components/dataadvisory/constants';
import { oneWeekAgoIsoString } from 'src/components/utils/dates';

// LF access validated resource type
export const ONLY_ISENGARD_ACCOUNTS_CAN_REQUEST_ACCESS = 'OnlyIsengardAccCanReqAccess';
export const ONLY_ISENGARD_PROD_ACCOUNTS_CAN_REQUEST_ACCESS = 'OnlyIsengardProdAccCanReqAccess';
export const CONSUMER_AWS_ACCOUNT_IS_ISENGARD = 'ConsumerAwsAccountIsIsengard';
export const CONSUMER_AWS_ACCOUNT_IS_PRODUCTION = 'ConsumerAwsAccountIsProduction';

export interface CartAccessItem {
  // resource information
  catalogId: string;
  catalogName: string;
  catalogDisplayName: string;
  databaseName: string;
  // user defined, custom dataset name.
  datasetName: string;
  region: string;
  tableName: string;
  clusterId?: string;
  schemaName?: string;
  redshiftWorkgroupName?: string;
  // other metadata
  itemType: string;
  dataLakePrincipal: string;
  dataOwnerRole: string;
  // data resource owner - group/wks
  owner: string;
  // unique cart resource id. eg: "lf_table_1.3502_lf_db.350266277334"
  id: string;
  // data owner publisher permission id
  permissionId: string;
  //datazone access
  dataZoneAssetId?: string;
  dataZoneConsumerProjectId?: string;
  // tag based access
  tagKey?: string;
  tagValue?: string;
  tag?: string;
  resources?: any[];
  // fine grained access
  fgapName?: string;
  // other unknown
  [key: string]: any;
}

export interface ApprovalsWorkflowMetadata {
  approvalTemplateId?: String;
  userAlias?: String;
  neededOnDate?: DateString;
  priority?: String;
  approvalWorkflowId?: String;
  approvalWorkflowStatus?: String;
  approvalDecisionTime?: String;
  approvalDecisionBy?: String;
  referenceId?: String;
  allowMergeApprovalWorkflow?: Boolean;
  autoApproval?: Boolean;
  requestContextId?: String;
}

export interface TemplateMetadata {
  templateId: string;
  resourceId: string;
  templateType: string;
  // template name
  name?: string;
  accessManagementInfo?: {
    simTemplates?: SimTicket[];
    approvalsWorkflowTemplate?: ApprovalsWorkflowMetadata;
  };
  accessRequestWorkflows?: {
    approvalsWorkflowTemplate?: ApprovalsWorkflowMetadata;
    conditionRule?: Rule;
    dataAgreement?: D3ADataAgreement;
    // user filled in DUA forms
    requestContextId?: string;
  }[];
  id: string;
  cartItemResIdToTemplResIdMap?: { [key: string]: any[] };
}

export const getResource = (cartItem: CartAccessItem): any => {
  if (!cartItem) return null;
  let resource = undefined;
  if (cartItem.itemType == RESOURCE_ITEM) {
    if (cartItem.datasetName !== ALL_TABLES_RES_NAME) {
      // table-level access
      // DataZone table-level
      const isDataZone = cartItem.dataZoneAssetId !== undefined && cartItem.dataZoneConsumerProjectId !== undefined;
      resource = {
        tableWithColumns: undefined,
        database: undefined,
        dataCellsFilter: undefined,
        lFTagPolicy: undefined,
        redshiftTagPolicy: undefined,
        dataZoneResource: !isDataZone
          ? undefined
          : {
              awsAccountId: cartItem.catalogId,
              clusterId: cartItem.clusterId,
              redshiftWorkgroupName: cartItem.redshiftWorkgroupName,
              databaseName: cartItem.databaseName,
              schemaName: cartItem.schemaName,
              tableName: cartItem.datasetName,
              region: cartItem.region,
            },
        table: isDataZone
          ? undefined
          : {
              databaseName: cartItem.databaseName,
              name: cartItem.datasetName,
              catalogId: cartItem.catalogId,
              tableWildcard: null,
            },
      };
    } else {
      // All Tables - level access
      resource = {
        tableWithColumns: null,
        database: null,
        dataCellsFilter: null,
        lFTagPolicy: null,
        redshiftTagPolicy: null,
        table: {
          databaseName: cartItem.databaseName,
          name: null,
          catalogId: cartItem.catalogId,
          tableWildcard: {},
        },
      };
    }
  } else if (cartItem.itemType == LAKE_FORMATION_TAG_ITEM) {
    resource = {
      tableWithColumns: null,
      table: null,
      database: null,
      dataCellsFilter: null,
      redshiftTagPolicy: null,
      lFTagPolicy: {
        resourceType: TABLE_RESOURCE_TYPE,
        expression: [{ tagKey: cartItem.tagKey, tagValues: [cartItem.tagValue] }],
        catalogId: cartItem.catalogId,
      },
    };
  } else if (cartItem.itemType == REDSHIFT_TAG_ITEM) {
    resource = {
      tableWithColumns: null,
      table: null,
      database: null,
      dataCellsFilter: null,
      redshiftTagPolicy: {
        tagKey: cartItem.tagKey,
        tagValue: cartItem.tagValue,
        catalogId: cartItem.catalogId,
      },
      lFTagPolicy: null,
    };
  } else if (cartItem.itemType == FGA_POLICY_TYPE_PREFIX) {
    resource = {
      tableWithColumns: null,
      table: null,
      database: null,
      dataCellsFilter: {
        clusterId: cartItem.clusterId,
        databaseName: cartItem.databaseName,
        schemaName: cartItem.schemaName,
        tableName: cartItem.tableName,
        tableCatalogId: cartItem.catalogId,
        name: cartItem.fgapName,
      },
      redshiftTagPolicy: null,
      lFTagPolicy: null,
    };
  }
  return resource;
};

export const setNotificationMessages = async (
  header,
  message,
  setNotifications: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  if (header === 'error') {
    setNotifications([
      {
        header: 'Error',
        type: 'error',
        content: message,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  } else {
    setNotifications([
      {
        type: 'success',
        content: message,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  }
};

export const getHCResourceIdForCartItem = (item: CartAccessItem): string => {
  let resourceId = '';
  // IAM dataset
  if (item.itemType == undefined || item.itemType == IAM_ACCESS_ITEM) {
    const datasetName = item['tableName'];
    const databaseName = item['databaseName'];
    const region = item['region'];
    const catalogId = item['catalogId'];
    resourceId = `DS-glue|A-${catalogId}|DN-${databaseName}|TN-${datasetName}|R-${region}`;
  }
  // Tag
  else if (item.itemType == LAKE_FORMATION_TAG_ITEM || item.itemType == REDSHIFT_TAG_ITEM) {
    resourceId = item['tagKey'] + ':' + item['tagValue'] + ':' + item['catalogId'] + ':' + item['region'];
  }
  // LakeFormation and DataZone dataset
  else if (item.itemType == RESOURCE_ITEM) {
    const datasetName = item['datasetName'];
    const databaseName = item['databaseName'];
    const region = item['region'];
    const catalogId = item['catalogId'];
    if (datasetName == ALL_TABLES_RES_NAME) {
      resourceId = `DS-glueLF|A-${catalogId}|DN-${databaseName}|R-${region}`;
    } else {
      resourceId = `DS-glueLF|A-${catalogId}|DN-${databaseName}|TN-${datasetName}|R-${region}`;
    }
    //for datazone onboard resources
    if (item['dataZoneAssetId'] != undefined) {
      resourceId = item['id'];
    }
  }
  // LakeFormation and Redshift FGAP
  else if (item.itemType == FGA_POLICY_TYPE_PREFIX) {
    resourceId = generateFGAPArn({
      id: item['id'],
      tableData: {
        clusterId: item['clusterId'],
        databaseName: item['databaseName'],
        region: item['region'],
        schemaName: item['schemaName'],
        tableCatalogId: item['catalogId'],
        tableName: item['tableName'],
      },
    });
  }
  return resourceId;
};

/**
 * Input: [['a', 1],['b', 2],['a', 3]];
 * Output:  { a: [1, 3], b: [2] }
 */
export const pairsToMapWithArrayValues = (pairs: [string, any][]): { [key: string]: any[] } => {
  return pairs.reduce((map, [key, value]) => {
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(value);
    return map;
  }, {});
};

export const getMatchedTemplatesForResource = async (
  resourceId: string,
  templateType: string,
  datalakePrincipals: string[],
  setNotifications: React.Dispatch<React.SetStateAction<any[]>>,
): Promise<MatchedTemplateWorkflow[]> => {
  let getTemplateForResourceResult: GetTemplateForResourceResult = null;
  try {
    getTemplateForResourceResult = await getTemplateForResource({
      resourceId: resourceId,
      templateType: templateType,
      dataLakePrincipals: datalakePrincipals,
    });
  } catch (e) {
    console.log('Error fetching template resources:', e);
    if (setNotifications) setNotificationMessages('error', 'Error fetching templates: ' + e.message, setNotifications);
    return [];
  }

  return getTemplateForResourceResult && getTemplateForResourceResult.matchedResources
    ? getTemplateForResourceResult.matchedResources
    : [];
};

export const validateAdditionalMetadataForm = (templates: TemplateMetadata[]) => {
  let validation: { key: string; isValid: boolean }[] = [];
  templates.map((t) => {
    if (t.accessManagementInfo?.simTemplates) {
      t.accessManagementInfo.simTemplates.map((st) => {
        if (!st.simTicketLink) {
          validation.push({ key: st.simTemplateName, isValid: false });
        } else {
          validation.push({ key: st.simTemplateName, isValid: true });
        }
      });
    }
    if (t.accessManagementInfo?.approvalsWorkflowTemplate) {
      if (!t.accessManagementInfo.approvalsWorkflowTemplate.neededOnDate) {
        validation.push({ key: 'approval wf needed date', isValid: false });
      } else if (!t.accessManagementInfo.approvalsWorkflowTemplate.priority) {
        validation.push({ key: 'approval wf priority', isValid: false });
      } else {
        validation.push({ key: 'approval wf', isValid: true });
      }
    }
    if (t.accessRequestWorkflows?.[0]) {
      if (t.accessRequestWorkflows?.[0].approvalsWorkflowTemplate) {
        let arw = t.accessRequestWorkflows?.[0].approvalsWorkflowTemplate;
        if (!arw.neededOnDate) {
          validation.push({ key: 'arw approval wf needed date', isValid: false });
        } else if (!arw.priority) {
          validation.push({ key: 'arw approval wf priority', isValid: false });
        } else if (!t.accessRequestWorkflows?.[0].requestContextId) {
          validation.push({ key: 'arw approval request context', isValid: false });
        } else {
          validation.push({ key: 'arw approval', isValid: true });
        }
      }
      if (t.accessRequestWorkflows?.[0].dataAgreement?.agreementId) {
        let arw = t.accessRequestWorkflows?.[0];
        if (!arw.requestContextId) {
          validation.push({ key: 'arw dua request context', isValid: false });
        } else {
          validation.push({ key: 'arw dua', isValid: true });
        }
      }
    }
  });
  let failedValidations = validation.filter((item) => item.isValid == false);
  if (failedValidations.length > 0) console.log('Failed validation, disabling the submit button:', failedValidations);
  // return true if invalid, button should be disabled.
  return failedValidations.length != 0;
};

export const updateTemplateInfo = async (
  hcId: string,
  activeWorkspace: any,
  username: string,
  setMatchedTemplates: any,
  principalsOverride?: string[],
): Promise<TemplateMetadata[]> => {
  let dataLakePrincipals: string[] = [];
  if (!!principalsOverride) {
    dataLakePrincipals = principalsOverride;
  } else {
    if (activeWorkspace && activeWorkspace.accountId) {
      dataLakePrincipals = [activeWorkspace.accountId];
    } else {
      dataLakePrincipals = [];
    }
  }
  // TODO, revisit priority of template type, post MAMAA MVP launch.
  // Check for AccessRequestWorkflows at first, if not found, then AccessManagement type
  const allMatchedTemplates = [];
  const matchedTemplatesARW = await getMatchedTemplatesForResource(
    hcId,
    TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
    dataLakePrincipals,
    null,
  );
  if (matchedTemplatesARW?.length > 0) {
    allMatchedTemplates.push(...matchedTemplatesARW);
  } else {
    console.log(`Fetching ${TEMPLATE_TYPE_ACCESS_MANAGEMENT} templates since none ARW type was found`);
    const matchedTemplatesAM = await getMatchedTemplatesForResource(
      hcId,
      TEMPLATE_TYPE_ACCESS_MANAGEMENT,
      dataLakePrincipals,
      null,
    );
    allMatchedTemplates.push(...matchedTemplatesAM);
  }

  setMatchedTemplates(allMatchedTemplates);

  return allMatchedTemplates.map((templateWorkflow: MatchedTemplateWorkflow) => {
    let accessManagement = undefined;
    let accessRequestWorkflows = undefined;
    if (templateWorkflow.templateType == TEMPLATE_TYPE_ACCESS_MANAGEMENT) {
      accessManagement = {};
      let simTickets = templateWorkflow.simTemplates?.map((simTemplate) => {
        return { ...simTemplate, simTicketLink: '' };
      });
      accessManagement['simTemplates'] = simTickets;

      if (
        templateWorkflow.approvalsWorkflowTemplate &&
        templateWorkflow.approvalsWorkflowTemplate.approvalTemplateTitle
      ) {
        accessManagement['approvalsWorkflowTemplate'] = extractApprovalMetadata(templateWorkflow, username);
      }
    }

    if (templateWorkflow.templateType == TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW) {
      const accessRequestWorkflow = {};
      if (
        templateWorkflow.approvalsWorkflowTemplate &&
        templateWorkflow.approvalsWorkflowTemplate.approvalTemplateTitle
      ) {
        accessRequestWorkflow['approvalsWorkflowTemplate'] = extractApprovalMetadata(templateWorkflow, username);
      }
      if (templateWorkflow.conditionRule && templateWorkflow.conditionRule.rule) {
        accessRequestWorkflow['conditionRule'] = templateWorkflow.conditionRule;
      }
      if (templateWorkflow.dataAgreement && templateWorkflow.dataAgreement.agreementId) {
        accessRequestWorkflow['dataAgreement'] = templateWorkflow.dataAgreement;
      }
      accessRequestWorkflows = [accessRequestWorkflow];
    }

    return {
      templateId: templateWorkflow.templateId,
      resourceId: templateWorkflow.resourceId,
      templateType: templateWorkflow.templateType,
      accessManagementInfo: accessManagement,
      accessRequestWorkflows: accessRequestWorkflows,
      id: hcId,
      name: templateWorkflow['templateName'],
    };
  });
};

const extractApprovalMetadata = (templateWorkflow: MatchedTemplateWorkflow, username: string) => {
  return {
    approvalTemplateId: extractApprovalTemplateIdFromLink(
      templateWorkflow.approvalsWorkflowTemplate.approvalTemplateLink,
    ),
    neededOnDate: '',
    priority: APPROVALS_PRIORITY,
    userAlias: username,
  };
};

export const findExistingRequestContexts = async (props: any) => {
  let agreementIdToRequestContextIdMap: Map<string, string> = new Map<string, string>();

  // Fetch request contexts that were created but not associated with any request in the past week.
  // This can happen if a user saves a new request context from checkout page, then leaves the page and comes back later.
  const listRequestContextsRequest: ListRequestContextsRequest = {
    ownerId: activeGroupOrWorkspaceId(props),
    type: 'D3ADataUsageAgreement',
    createDateRange: {
      startDate: oneWeekAgoIsoString(),
    },
  };
  const listRequestContextsResponse: ListRequestContextsResponse = await listRequestContexts(
    listRequestContextsRequest,
  );
  const pendingRequestContextsFromPastWeek = listRequestContextsResponse.RequestContexts.filter(
    (requestContext) => !requestContext.accessRequestIds || requestContext.accessRequestIds.length == 0,
  );
  // Set them in the agreementId -> requestContextId map
  pendingRequestContextsFromPastWeek.forEach((requestContext) => {
    if (requestContext.requestSchema?.dataAgreementId) {
      console.log(
        `Found unused reqContext DUA for agreementId:${requestContext.requestSchema.dataAgreementId} with reqContextId:${requestContext.requestContextId}`,
      );
      agreementIdToRequestContextIdMap.set(
        requestContext.requestSchema.dataAgreementId,
        requestContext.requestContextId,
      );
    }
  });

  // Fetch request contexts that are associated with a pending request the user has.
  // This can happen if the user requests access to one dataset, then later comes back and requests access to another before the first one is approved.
  let pendingRequests: GladstoneRequestItem[] = [];
  if (!signedInWithWorkspace(props)) {
    // Group level - use GetRequestedByRequests API
    let nextToken: string = undefined;
    do {
      const getRequestsRequest: GetRequestsRequest = {
        groupId: props.activeGroup,
        requestType: 'CreateRequestForDataPermission',
        nextToken,
      };
      const getRequestsResponse: GetRequestsResult = await getRequestedByRequests(getRequestsRequest);
      pendingRequests = [...pendingRequests, ...getRequestsResponse.requestList];
      nextToken = getRequestsResponse.nextToken;
    } while (!!nextToken && nextToken.length > 0);
  } else {
    // Workspace level - use GetDataPermissionRequests API
    let nextToken: string = undefined;
    do {
      const listRequestsRequest: GetDataPermissionRequestsRequest = {
        isIncomingRequests: false,
        includesRequestHistory: false,
        workspaceId: activeGroupOrWorkspaceId(props),
      };
      const listRequestsResponse: GetDataPermissionRequestsResult = await getDataPermissionRequests(
        listRequestsRequest,
      );
      pendingRequests = [...pendingRequests, ...listRequestsResponse.requestList];
      nextToken = listRequestsResponse.nextToken;
    } while (!!nextToken && nextToken.length > 0);
  }

  // Now filter the pending requests where DUA ID matches.
  const pendingRequestContextsFromActiveRequests = pendingRequests
    .filter((request) => !!request.additionalMetadata)
    .map((request) => request.additionalMetadata)
    .filter((metadata) => !!metadata.accessRequestWorkflowInfoList)
    .filter((metadata) => metadata.accessRequestWorkflowInfoList.length > 0);
  pendingRequestContextsFromActiveRequests.forEach((metadata) => {
    metadata.accessRequestWorkflowInfoList.forEach((arw) => {
      // Filter them out if (has approval workflow) AND (approval auto merge is false)
      if (arw.approvalTemplateId != undefined && arw.approvalTemplateId.length > 0 && !arw.allowMergeApprovalWorkflow) {
        return;
      }
      // Set them in the agreementId -> requestContextId map
      if (arw.agreementId && arw.requestContextId) {
        console.log(
          `Found pending reqContext DUA for agreementId:${arw.agreementId} with reqContextId:${arw.requestContextId}`,
        );
        agreementIdToRequestContextIdMap.set(arw.agreementId, arw.requestContextId);
      }
    });
  });

  return agreementIdToRequestContextIdMap;
};

export const applyExistingRequestContextsToAdditionalMetadata = (
  additionalMetadata: TemplateMetadata[],
  agreementIdToRequestContextIdMap: Map<string, string>,
) => {
  let newAdditionalMetadata: TemplateMetadata[] = JSON.parse(JSON.stringify(additionalMetadata));
  newAdditionalMetadata.forEach((metadata) => {
    if (metadata.accessRequestWorkflows?.length > 0) {
      metadata.accessRequestWorkflows.forEach((arw) => {
        if (!arw.requestContextId && agreementIdToRequestContextIdMap.has(arw.dataAgreement.agreementId)) {
          arw.requestContextId = agreementIdToRequestContextIdMap.get(arw.dataAgreement.agreementId);
        }
      });
    }
  });
  return newAdditionalMetadata;
};
